import { memo, useCallback, useState, useEffect } from 'react'
import { Heart, MessageCircle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useGallery } from '../context/GalleryContext'
import { useProgressiveImage } from '../hooks/useProgressiveImage'
import type { GalleryImage as GalleryImageType, TargetResolution } from '../types'
import SubscriptionGate from './SubscriptionGate'
import GalleryPurchaseModal from './GalleryPurchaseModal'
import { gridGalleryConfig } from '../config/gridGalleryConfig'

interface GridImageProps {
  image: GalleryImageType
  visibleCount: number
  columns: number
  onClick: () => void
}

/**
 * Determine target resolution based on how many images are visible on screen.
 */
export function getGridTargetResolution(visibleCount: number): TargetResolution {
  const { qualityThresholds } = gridGalleryConfig
  if (visibleCount <= qualityThresholds.full) return 'full'
  if (visibleCount <= qualityThresholds.medium) return 'medium'
  return 'thumbnail'
}

function GridImage({ image, visibleCount, columns, onClick }: GridImageProps) {
  const {
    refreshImageUrl,
    likedImageIds,
    toggleLike,
    markImageAsPurchased,
    purchasedImageIds,
  } = useGallery()
  const navigate = useNavigate()
  const isLiked = likedImageIds.has(image.id)
  const isPurchased = purchasedImageIds.has(image.id)
  const targetResolution = getGridTargetResolution(visibleCount)

  const [showPurchaseModal, setShowPurchaseModal] = useState(false)

  const handlePurchaseClick = useCallback(() => {
    setShowPurchaseModal(true)
  }, [])

  const handlePurchaseSuccess = useCallback(() => {
    markImageAsPurchased(image.id)
  }, [markImageAsPurchased, image.id])

  // For locked images when zoomed out (no overlay), only show the blurry placeholder.
  // When zoomed in (overlay visible), allow thumbnail so the backdrop-blur looks right.
  const isLocked = !image.hasAccess && image.access_level !== 'public'
  const showOverlay = isLocked && columns <= gridGalleryConfig.premiumOverlayMaxColumns
  const showPlaceholderOnly = isLocked && !showOverlay

  const {
    displayedSrc: _displayedSrc,
    loadingSrc: _loadingSrc,
    isTransitioning: _isTransitioning,
    handleError,
    onTransitionEnd,
  } = useProgressiveImage(image, true, targetResolution, refreshImageUrl)

  // Override: locked images zoomed out only show the blurry placeholder
  const displayedSrc = showPlaceholderOnly ? (image.urls.placeholder || null) : _displayedSrc
  const loadingSrc = showPlaceholderOnly ? null : _loadingSrc
  const isTransitioning = showPlaceholderOnly ? false : _isTransitioning

  // Auto-complete transition after a short delay (backup for onTransitionEnd)
  useEffect(() => {
    if (isTransitioning && loadingSrc) {
      const timer = setTimeout(onTransitionEnd, 350)
      return () => clearTimeout(timer)
    }
  }, [isTransitioning, loadingSrc, onTransitionEnd])

  // Show action buttons only when cells are large enough
  const showButtons = columns <= 5

  return (
    <div
      className="relative overflow-hidden cursor-pointer"
      style={{
        aspectRatio: `${gridGalleryConfig.cellAspectRatio}`,
        backgroundColor: image.dominant_color || '#1f2937',
        contain: 'layout style paint',
      }}
      onClick={onClick}
    >
      {/* Skeleton shimmer while no image */}
      {!displayedSrc && !loadingSrc && (
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite]"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
            }}
          />
        </div>
      )}

      {/* Base layer: currently displayed image - contain, so nothing is cropped;
          the cell is 9:16 like almost every photo, others get bars */}
      {displayedSrc && (
        <img
          src={displayedSrc}
          alt={image.title || ''}
          onError={handleError}
          className="absolute inset-0 w-full h-full object-contain"
          style={{
            imageRendering: 'auto',
            WebkitBackfaceVisibility: 'hidden',
            backfaceVisibility: 'hidden',
            ...(isLocked ? { filter: 'blur(10px)', transform: 'scale(1.1)' } : {}),
          }}
          loading="lazy"
          decoding="async"
          draggable={false}
        />
      )}

      {/* Top layer: New image fading in */}
      {loadingSrc && isTransitioning && (
        <img
          src={loadingSrc}
          alt={image.title || ''}
          onTransitionEnd={onTransitionEnd}
          className="absolute inset-0 w-full h-full object-contain transition-opacity duration-300"
          style={{
            opacity: 1,
            imageRendering: 'auto',
            WebkitBackfaceVisibility: 'hidden',
            backfaceVisibility: 'hidden',
          }}
          loading="eager"
          decoding="async"
          draggable={false}
        />
      )}

      {/* Subscription gate overlay — only shown when zoomed in enough */}
      {showOverlay && (
        <SubscriptionGate
          accessLevel={image.access_level}
          price={image.price}
          onPurchase={handlePurchaseClick}
        />
      )}

      {/* Unlocked badge */}
      {isPurchased && image.access_level === 'premium' && (
        <div className="absolute top-1 left-1 z-10 bg-[#4AA3CC] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
          UNLOCKED
        </div>
      )}

      {/* Purchase modal */}
      {showPurchaseModal && (
        <GalleryPurchaseModal
          image={image}
          onClose={() => setShowPurchaseModal(false)}
          onSuccess={handlePurchaseSuccess}
        />
      )}

      {/* Like button - only shown when cells are large enough */}
      {showButtons && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            toggleLike(image.id)
          }}
          className="absolute bottom-1.5 right-1.5 z-10 w-7 h-7 bg-black/40 hover:bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center transition-all active:scale-95"
          aria-label={isLiked ? 'Unlike' : 'Like'}
          style={{ touchAction: 'manipulation' }}
        >
          <Heart
            size={14}
            className={isLiked ? 'text-red-500 fill-red-500' : 'text-white/80 hover:text-white'}
          />
        </button>
      )}

      {/* Message button - only shown when cells are large enough and image is unlocked */}
      {showButtons && !isLocked && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            navigate(`/messages?replyToGalleryImage=${image.id}`)
          }}
          className="absolute bottom-1.5 left-1.5 z-10 w-7 h-7 bg-black/40 hover:bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center transition-all active:scale-95"
          aria-label="Send message about this image"
        >
          <MessageCircle size={14} className="text-white/80 hover:text-white" />
        </button>
      )}

      <style>{`
        @keyframes shimmer {
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  )
}

// Memoized: only re-render when meaningful props change
export default memo(GridImage, (prev, next) => {
  const threshold = gridGalleryConfig.premiumOverlayMaxColumns
  return (
    prev.image.id === next.image.id &&
    prev.image.hasAccess === next.image.hasAccess &&
    prev.image.price === next.image.price &&
    prev.columns === next.columns &&
    (prev.columns <= threshold) === (next.columns <= threshold) &&
    getGridTargetResolution(prev.visibleCount) === getGridTargetResolution(next.visibleCount)
  )
})
